import { memo, useMemo, useRef, useState } from 'react';
import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';

interface ChipsSelectEditorProps {
    ruleId: string;
    values: FieldValue[];
    selectedValues: string[];
    onChange: (value: string[]) => void;
}

const CHECKBOX_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKBOX_CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const CHECKBOX_INDETERMINATE_ICON = <IndeterminateCheckBoxIcon fontSize="small" />;
const LISTBOX_PROPS = { style: { maxHeight: 300 } };

// Synthetic option prepended to the (already filtered) dropdown list — never part of `values`, so
// it can never end up in `selectedValues`/be sent to the backend; handleChange below intercepts it
// and turns it into a select/unselect of exactly the options currently shown (i.e. matching the
// active search, not the whole field), instead of letting Autocomplete treat it as a real selection.
const SELECT_ALL_NAME = '__select_all__';

const normalizeForSearch = (value: string): string =>
    value.trim().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

// MUI vertically centers the clear/arrow adornment against the whole input box by default
// (`top: 50%; transform: translate(0, -50%)`), which is right for one line of chips but drifts down
// into the middle of the block once chips wrap onto extra lines. The horizontal space for it is
// already correctly reserved by MUI itself (padding-right on the same root, scaled to how many
// icons are shown) — only the vertical centering needs fixing: pin it near the input's first line
// instead, so it stays in the same top-right spot no matter how tall the wrapped chips make the box.
const AUTOCOMPLETE_SX = {
    '& .MuiAutocomplete-inputRoot': {
        flexWrap: 'wrap',
        alignItems: 'center',
        py: 0.5,
    },
    '& .MuiAutocomplete-endAdornment': {
        top: '6px',
        transform: 'none',
    },
    '& .MuiChip-root': {
        fontSize: { xs: '0.7rem', sm: '0.8125rem' },
        height: { xs: 22, sm: 24 },
    },
};

// Collapsed by default, opens into a searchable checkbox dropdown and renders selections as chips
// inside the input — matches the legacy multi-select look instead of the always-expanded checkbox
// list (CheckboxListPicker), which was correct for the simple builder's inline layout but too heavy
// for the advanced rule editor's one-line-per-rule layout.
const ChipsSelectEditor = memo(function ChipsSelectEditor({ ruleId, values, selectedValues, onChange }: ChipsSelectEditorProps) {
    const { t } = useLanguage();
    const selectedOptions = values.filter(option => selectedValues.includes(option.name));

    // Autocomplete (multiple mode) always wants to blank the search text right after any selection
    // commits — selecting/unselecting an option (reason 'selectOption'/'removeOption'), or the
    // value-changed effect that follows it (reason 'reset'), both call onInputChange with '' since
    // getInputValue always resolves to '' for multi-select. Controlling inputValue ourselves and
    // only actually applying an update for reason 'input' (real typing) is what keeps the search box
    // — and therefore what "select all" scopes to — intact across any selection, "select all" included.
    const [inputValue, setInputValue] = useState('');

    const selectAllOption: FieldValue = useMemo(() => ({
        name: SELECT_ALL_NAME,
        label: t('pages.clients.queryBuilder.simple.selectAll'),
    }), [t]);

    // filterOptions runs once per render/keystroke with the current search — stashing its result
    // (the real options, sentinel excluded) is what lets the "select all" row and its click handler
    // below know exactly which options are "currently shown" without recomputing the search here too.
    const lastFilteredRef = useRef<FieldValue[]>(values);

    const filterOptions = (options: FieldValue[], state: { inputValue: string }) => {
        const query = normalizeForSearch(state.inputValue);
        const filtered = query
            ? options.filter(option => normalizeForSearch(option.label).includes(query))
            : options;
        lastFilteredRef.current = filtered;
        return filtered.length > 0 ? [selectAllOption, ...filtered] : filtered;
    };

    const handleChange = (_e: unknown, newValue: FieldValue[]) => {
        if (!newValue.some(option => option.name === SELECT_ALL_NAME)) {
            onChange(newValue.map(option => option.name));
            return;
        }
        const filteredNames = lastFilteredRef.current.map(option => option.name);
        const allFilteredSelected = filteredNames.every(name => selectedValues.includes(name));
        onChange(
            allFilteredSelected
                ? selectedValues.filter(name => !filteredNames.includes(name))
                : Array.from(new Set([...selectedValues, ...filteredNames]))
        );
    };

    return (
        <Autocomplete
            multiple
            fullWidth
            size="small"
            sx={AUTOCOMPLETE_SX}
            id={`${ruleId}-tags-select`}
            options={values}
            value={selectedOptions}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={(_e, newInputValue, reason) => {
                if (reason !== 'input' && reason !== 'clear') return;
                setInputValue(newInputValue);
            }}
            filterOptions={filterOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.name === val.name}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
                if (option.name === SELECT_ALL_NAME) {
                    const filteredNames = lastFilteredRef.current.map(o => o.name);
                    const allSelected = filteredNames.length > 0 && filteredNames.every(name => selectedValues.includes(name));
                    const someSelected = !allSelected && filteredNames.some(name => selectedValues.includes(name));
                    return (
                        <li {...props} key={SELECT_ALL_NAME} style={{ fontWeight: 700, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                            <Checkbox
                                icon={CHECKBOX_ICON}
                                checkedIcon={CHECKBOX_CHECKED_ICON}
                                indeterminateIcon={CHECKBOX_INDETERMINATE_ICON}
                                checked={allSelected}
                                indeterminate={someSelected}
                                size="small"
                                sx={{ mr: 1 }}
                            />
                            {option.label}
                        </li>
                    );
                }
                return (
                    <li {...props} key={option.name}>
                        <Checkbox
                            icon={CHECKBOX_ICON}
                            checkedIcon={CHECKBOX_CHECKED_ICON}
                            checked={selected}
                            size="small"
                            sx={{ mr: 1 }}
                        />
                        {option.label}
                    </li>
                );
            }}
            renderTags={(tagValues, getTagProps) =>
                tagValues.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.name} label={option.label} size="small" />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={selectedOptions.length === 0 ? t('pages.clients.queryBuilder.selectPlaceholder') : undefined}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
            noOptionsText={t('pages.clients.queryBuilder.simple.noOptionsFound')}
            ListboxProps={LISTBOX_PROPS}
        />
    );
});

export default ChipsSelectEditor;
